<template>
  <v-dialog v-model="reportDialog" max-width="1200" persistent scrollable>
    <v-card>
      <v-card-title class="dialog-header">
        <v-icon class="mr-3" color="white" size="28">mdi-file-download</v-icon>
        <span class="dialog-title">Download Bus Change Report</span>
        <v-spacer></v-spacer>
        <v-btn class="white--text" icon dark @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-4">
        <!-- Filters - All in One Line -->
        <v-layout row align-center class="mb-2 report-filters-row">
          <!-- Building Filter -->
          <v-flex class="report-filter-building pr-1" v-if="showReportBuildingField">
            <v-autocomplete
              v-model="reportFilterBuildings"
              :items="reportBuildings"
              label="Building"
              outlined
              dense
              clearable
              multiple
              chips
              deletable-chips
              small-chips
              hide-details
              no-data-text="No buildings"
              @change="applyReportFilters"
            ></v-autocomplete>
          </v-flex>

          <!-- Branch Selection -->
          <v-flex class="report-filter-narrow pr-1" v-if="showReportBranchField">
            <v-autocomplete
              v-model="reportFilterBranch"
              :items="reportBranches"
              label="Branch"
              outlined
              dense
              clearable
              hide-details
              @change="applyReportFilters"
            ></v-autocomplete>
          </v-flex>

          <!-- Class Filter -->
          <v-flex class="report-filter-grade pr-2">
            <v-autocomplete
              v-model="reportFilterClass"
              :items="reportClasses"
              label="Grade"
              prepend-inner-icon="mdi-chair-school"
              outlined
              dense
              clearable
              multiple
              chips
              deletable-chips
              small-chips
              hide-details
              :no-data-text="
                reportFilterBranch
                  ? 'No grades available'
                  : 'Please select branch first'
              "
              @change="applyReportFilters"
            ></v-autocomplete>
          </v-flex>

          <!-- Bus Number Filter -->
          <v-flex class="report-filter-main pr-2">
            <v-autocomplete
              v-model="reportFilterBusNumber"
              :items="reportBusNumbers"
              label="Bus #"
              prepend-inner-icon="mdi-bus"
              outlined
              dense
              clearable
              multiple
              chips
              deletable-chips
              small-chips
              hide-details
              @change="applyReportFilters"
            ></v-autocomplete>
          </v-flex>

          <!-- Start Date Filter -->
          <v-flex class="report-filter-date pr-1">
            <v-menu
              v-model="reportStartDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              max-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-model="reportFilterStartDate"
                  label="Start Date"
                  prepend-inner-icon="mdi-calendar"
                  outlined
                  dense
                  clearable
                  readonly
                  hide-details
                  v-on="on"
                  @click:clear="clearStartDateFilter"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="reportFilterStartDate"
                no-title
                @input="
                  reportStartDateMenu = false;
                  applyReportFilters();
                "
              ></v-date-picker>
            </v-menu>
          </v-flex>

          <!-- End Date Filter -->
          <v-flex class="report-filter-date">
            <v-menu
              v-model="reportEndDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              max-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-model="reportFilterEndDate"
                  label="End Date"
                  prepend-inner-icon="mdi-calendar"
                  outlined
                  dense
                  clearable
                  readonly
                  hide-details
                  v-on="on"
                  @click:clear="clearEndDateFilter"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="reportFilterEndDate"
                no-title
                @input="
                  reportEndDateMenu = false;
                  applyReportFilters();
                "
              ></v-date-picker>
            </v-menu>
          </v-flex>
        </v-layout>

        <!-- Bus Changes Selection Table -->
        <div class="report-table-container mt-3">
          <div class="report-table-header">
            <span class="body-2" v-if="loadingReportBusChanges">Loading...</span>
            <span class="body-2" v-else>
              <strong>{{ selectedReportBusChanges.length }}</strong> of
              <strong>{{ filteredReportBusChanges.length }}</strong> selected
            </span>
            <div class="d-flex align-center">
              <v-checkbox
                v-model="onlyNotDownloadedBusChanges"
                label="Hide old downloaded"
                color="success"
                hide-details
                class="mt-0 pt-0 mr-3"
                @change="applyReportFilters"
              ></v-checkbox>
              <v-checkbox
                v-model="showCompletedBusChanges"
                label="Show Completed"
                color="success"
                hide-details
                class="mt-0 pt-0 mr-3"
                @change="applyReportFilters"
              ></v-checkbox>
              <v-checkbox
                v-if="isAdmin()"
                v-model="showDeletedBusChanges"
                label="Show Deleted"
                color="error"
                hide-details
                class="mt-0 pt-0 mr-3"
                @change="applyReportFilters"
              ></v-checkbox>
              <v-btn
                small
                flat
                class="my-0"
                color="success"
                @click="selectAllReportBusChanges"
                :disabled="filteredReportBusChanges.length === 0"
              >
                Select All
              </v-btn>
              <v-btn
                flat
                small
                class="my-0"
                @click="clearAllReportBusChanges"
                :disabled="selectedReportBusChanges.length === 0"
              >
                Clear
              </v-btn>
            </div>
          </div>
          <div class="report-table-scroll">
            <div v-if="loadingReportBusChanges" class="report-list-loading">
              <v-progress-circular
                indeterminate
                color="success"
                size="32"
              ></v-progress-circular>
              <div class="grey--text mt-2">Loading bus changes...</div>
            </div>
            <v-data-table
              v-show="!loadingReportBusChanges"
              v-model="selectedReportBusChanges"
              :headers="reportBusChangesHeaders"
              :items="filteredReportBusChanges"
              item-key="id"
              select-all
              :pagination.sync="reportPagination"
              class="elevation-1 report-data-table"
              hide-actions
            >
              <template v-slot:items="props">
                <td>
                  <v-checkbox
                    v-model="props.selected"
                    color="success"
                    hide-details
                  ></v-checkbox>
                </td>
                <td>
                  <div class="bus-change-main-info">
                    {{ props.item.studentNameHeb || props.item.studentName }}
                    <span
                      v-if="isEndedBusChange(props.item)"
                      class="report-ended-chip"
                      :class="
                        isFinishedToday(props.item)
                          ? 'report-ended-chip-today'
                          : ''
                      "
                    >
                      {{
                        isFinishedToday(props.item) ? "Ends today" : "Ended"
                      }}
                    </span>
                  </div>
                  <div class="bus-change-sub-info">
                    <span v-if="props.item.busChangeName"
                      ><v-icon x-small>mdi-account</v-icon>
                      {{ props.item.busChangeName }}</span
                    >
                    <span v-if="props.item.busChangeAddress"
                      ><v-icon x-small>mdi-map-marker</v-icon>
                      {{ props.item.busChangeAddress }}</span
                    >
                    <span v-if="props.item.status">
                      <v-icon
                        x-small
                        :color="getStatusIconColor(props.item.status)"
                        >{{ getStatusIcon(props.item.status) }}</v-icon
                      >
                      {{ props.item.status }}
                    </span>
                  </div>
                </td>
                <td>{{ props.item.branch }}</td>
                <td>{{ props.item.building }}</td>
                <td>{{ props.item.class }}</td>
                <td>{{ props.item.busNumber }}</td>
                <td>{{ formatDate(props.item.startDate) }}</td>
                <td>{{ formatDate(props.item.endDate) }}</td>
              </template>
            </v-data-table>
          </div>
          <div class="report-table-pagination">
            <v-pagination
              v-if="reportPaginationPages > 1"
              v-model="reportPagination.page"
              :length="reportPaginationPages"
              :total-visible="7"
              color="success"
            ></v-pagination>
          </div>
        </div>
      </v-card-text>

      <v-card-actions class="pb-3 pt-2">
        <v-spacer></v-spacer>
        <v-btn color="default" dark @click="close">Cancel</v-btn>
        <v-btn
          color="info"
          @click="downloadReport('labels30')"
          :loading="downloadingReport === 'labels30'"
          :disabled="
            selectedReportBusChanges.length === 0 ||
              (downloadingReport && downloadingReport !== 'labels30')
          "
        >
          <v-icon left>mdi-tag-multiple</v-icon>
          Extended Bus Changes
        </v-btn>
        <v-btn
          color="success"
          @click="downloadReport('labelSize')"
          :loading="downloadingReport === 'labelSize'"
          :disabled="
            selectedReportBusChanges.length === 0 ||
              (downloadingReport && downloadingReport !== 'labelSize')
          "
        >
          <v-icon left>mdi-file-download</v-icon>
          Single-Day &amp; Bus Rabbi ({{ selectedReportBusChanges.length }})
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from "moment";

export default {
  name: "BusChangeReportDialog",

  data: () => ({
    reportDialog: false,
    reportBranches: [],
    reportClasses: [],
    reportBusNumbers: [],

    reportFilterBranch: null,
    reportFilterBuildings: [],
    reportFilterClass: [],
    reportFilterBusNumber: [],
    reportFilterStartDate: null,
    reportFilterEndDate: null,
    reportStartDateMenu: false,
    reportEndDateMenu: false,
    showCompletedBusChanges: false,
    showDeletedBusChanges: false,
    onlyNotDownloadedBusChanges: true,

    reportBusChanges: [],
    reportBusChangesFullData: [],
    filteredReportBusChanges: [],
    selectedReportBusChanges: [],
    loadingReportBusChanges: false,
    reportPagination: {
      page: 1,
      rowsPerPage: 10,
      rowsPerPageItems: [10, 25, 50, 100],
    },
    reportBusChangesHeaders: [
      { text: "Student Name", value: "studentName", sortable: true },
      { text: "Branch", value: "branch", sortable: true },
      { text: "Building", value: "building", sortable: true },
      { text: "Class", value: "class", sortable: true },
      { text: "Bus Number", value: "busNumber", sortable: true },
      { text: "Start Date", value: "startDate", sortable: true },
      { text: "End Date", value: "endDate", sortable: true },
    ],

    loadingReportOptions: false,
    downloadingReport: null,

    busRoutes: [],
    summerDates: null,
    userDayCampBuildings: [],
    busChangeGrades: [],
  }),

  computed: {
    userBranch() {
      const branch = this.$store.state.authentication.branch;
      if (branch && !branch.includes("null")) {
        return branch;
      }
      return null;
    },
    showReportBranchField() {
      return !this.userBranch || this.userBranch.includes(",");
    },
    reportBuildings() {
      const seen = {};
      (this.reportBusChanges || []).forEach((bc) => {
        const b = String((bc && bc.building) || "").trim();
        if (b) seen[b.toUpperCase()] = b;
      });
      return Object.keys(seen)
        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
        .map((key) => seen[key]);
    },
    showReportBuildingField() {
      if (this.isAdmin() || this.canManageRoutePresets()) return true;
      if (this.userAssignedBranchCount() > 1) return true;
      if ((this.userDayCampBuildings || []).length > 1) return true;
      return this.reportBuildings.length > 1;
    },
    reportPaginationPages() {
      if (!this.filteredReportBusChanges.length) return 1;
      return Math.ceil(
        this.filteredReportBusChanges.length / this.reportPagination.rowsPerPage
      );
    },
    sortedReportBusChanges() {
      const items = [...this.filteredReportBusChanges];

      if (this.reportPagination.sortBy) {
        const sortKey = this.reportPagination.sortBy;
        const descending = this.reportPagination.descending;

        items.sort((a, b) => {
          let aVal = a[sortKey];
          let bVal = b[sortKey];

          if (aVal == null) aVal = "";
          if (bVal == null) bVal = "";

          if (sortKey === "startDate" || sortKey === "endDate") {
            aVal = new Date(aVal).getTime() || 0;
            bVal = new Date(bVal).getTime() || 0;
          }

          if (sortKey === "busNumber") {
            const aNum = parseInt(aVal);
            const bNum = parseInt(bVal);
            if (!isNaN(aNum) && !isNaN(bNum)) {
              aVal = aNum;
              bVal = bNum;
            }
          }

          if (aVal < bVal) return descending ? 1 : -1;
          if (aVal > bVal) return descending ? -1 : 1;
          return 0;
        });
      }

      return items;
    },
    summerSeasonName() {
      const year =
        this.summerDates && this.summerDates.startDate
          ? moment(this.summerDates.startDate).year()
          : moment().year();
      return `Summer ${year}`;
    },
  },

  watch: {
    reportFilterBranch(newBranch) {
      if (newBranch) {
        this.reportClasses = [];
        this.reportFilterClass = [];
        this.loadReportClasses();
      }
    },
    reportFilterBuildings() {
      this.saveReportBuildingFilter();
    },
  },

  methods: {
    async open() {
      this.reportDialog = true;
      this.loadingReportBusChanges = true;
      this.reportBusChanges = [];
      this.filteredReportBusChanges = [];
      this.selectedReportBusChanges = [];
      const today = moment().format("YYYY-MM-DD");
      this.reportFilterStartDate = today;
      this.reportFilterEndDate = today;
      this.showCompletedBusChanges = false;
      this.showDeletedBusChanges = false;
      this.onlyNotDownloadedBusChanges = true;
      this.restoreReportBuildingFilter();

      try {
        await Promise.all([
          this.loadUserDayCampBuildings(),
          this.loadSummerDates(),
          this.loadBusChangeGrades(),
        ]);

        await Promise.all([
          this.loadReportBranches(),
          this.loadReportBusNumbers(),
          this.loadReportBusChanges(),
        ]);

        if (this.userBranch && !this.userBranch.includes(",")) {
          this.reportFilterBranch = this.userBranch;
        }

        if (this.reportFilterBranch) {
          await this.loadReportClasses();
        }

        this.applyReportFilters();
        this.selectAllReportBusChanges();
      } catch (error) {
        console.error("Error opening bus change report:", error);
        this.loadingReportBusChanges = false;
      }
    },

    close() {
      this.reportDialog = false;
      this.reportFilterBranch = null;
      this.reportFilterClass = [];
      this.reportFilterBusNumber = [];
      this.reportFilterStartDate = null;
      this.reportFilterEndDate = null;
      this.showCompletedBusChanges = false;
      this.showDeletedBusChanges = false;
      this.onlyNotDownloadedBusChanges = true;
      this.reportClasses = [];
      this.reportBusChanges = [];
      this.reportBusChangesFullData = [];
      this.filteredReportBusChanges = [];
      this.selectedReportBusChanges = [];
      this.reportPagination.page = 1;
    },

    isAdmin() {
      const isAdmin = this.$store.getters["authentication/isAdmin"];
      if (!isAdmin || isAdmin == "false") return false;
      return true;
    },

    canManageRoutePresets() {
      if (this.isAdmin()) return true;
      const roles = this.$store.state.authentication.roles;
      const list = Array.isArray(roles)
        ? roles
        : roles
        ? String(roles).split(",")
        : [];
      return list.map((r) => String(r).trim()).includes("Transportation");
    },

    restoreReportBuildingFilter() {
      try {
        const raw = localStorage.getItem("busChangeReport.buildings");
        const stored = raw ? JSON.parse(raw) : null;
        this.reportFilterBuildings = Array.isArray(stored)
          ? stored.map((b) => String(b || "").trim()).filter(Boolean)
          : [];
      } catch (e) {
        this.reportFilterBuildings = [];
      }
    },

    saveReportBuildingFilter() {
      try {
        localStorage.setItem(
          "busChangeReport.buildings",
          JSON.stringify(this.reportFilterBuildings || [])
        );
      } catch (e) {
        // ignore quota / private mode
      }
    },

    userAssignedBranchCount() {
      return (this.userBranch || "")
        .split(",")
        .map((b) => b.trim())
        .filter(Boolean).length;
    },

    async loadUserDayCampBuildings() {
      const username = this.$store.state.authentication.user;

      if (!username) {
        this.userDayCampBuildings = [];
        return;
      }

      try {
        const attributes = await this.$http.get(
          "api/users/GetUserAttributesByUsername?username=" +
            encodeURIComponent(username)
        );

        this.userDayCampBuildings = (attributes || [])
          .filter((a) => a && a.name && a.name.trim() === "DayCampBuilding")
          .map((a) => (a.value ? a.value.trim() : ""))
          .filter(Boolean);
      } catch (e) {
        this.userDayCampBuildings = [];
      }
    },

    hasUserDayCampBuildings() {
      return !this.isAdmin() && this.userDayCampBuildings.length > 0;
    },

    useDayCampBuildingFilter() {
      return this.hasUserDayCampBuildings() && this.isWithinSummerSeason();
    },

    async loadSummerDates() {
      if (this.summerDates) return;
      try {
        const response = await this.$http.get("api/daycamp/getSummerDates");
        if (response && response.startDate && response.endDate) {
          this.summerDates = {
            startDate: response.startDate,
            endDate: response.endDate,
          };
        }
      } catch (e) {
        this.summerDates = null;
      }
    },

    isWithinSummerSeason() {
      if (!this.summerDates) return false;
      const today = moment().startOf("day");
      const start = moment(this.summerDates.startDate).startOf("day");
      const end = moment(this.summerDates.endDate).startOf("day");
      if (!start.isValid() || !end.isValid()) return false;
      return today.isSameOrAfter(start) && today.isSameOrBefore(end);
    },

    async loadBusRoutes() {
      try {
        let url = "api/buschange/GetBusRoutes";
        if (this.isWithinSummerSeason()) {
          url +=
            `?season=${encodeURIComponent(this.summerSeasonName)}` +
            "&kind=DayCamp";
        }
        this.busRoutes = await this.$http.get(url);
      } catch (e) {
        this.busRoutes = [];
      }
    },

    getRouteBusNumber(route) {
      if (!route) return "";
      const actual = (route.actualBusNumber || "").toString().trim();
      if (actual) return actual;
      return (route.busNumber || "").toString().trim();
    },

    async loadReportBranches() {
      if (this.reportBranches.length > 0) return;

      try {
        this.loadingReportOptions = true;
        const response = await this.$http.get("odata/branches");
        let branches = response.value
          .filter((b) => !b.inactive)
          .map((b) => b.branchCode);

        if (this.userBranch) {
          const allowedBranches = this.userBranch
            .split(",")
            .map((b) => b.trim());
          branches = branches.filter((b) => allowedBranches.includes(b));
        }

        this.reportBranches = branches;
      } catch (error) {
        console.error("Error loading branches:", error);
        this.$notify({
          type: "error",
          text: "Failed to load branches",
        });
      } finally {
        this.loadingReportOptions = false;
      }
    },

    async loadReportClasses() {
      if (!this.reportFilterBranch) return;
      if (this.reportClasses.length > 0) return;

      try {
        this.loadingReportOptions = true;
        const grades = await this.$http.get(
          `api/students/getGradeList?branch=${this.reportFilterBranch}`
        );
        this.reportClasses = grades.sort();
      } catch (error) {
        console.error("Error loading classes:", error);
        this.$notify({
          type: "error",
          text: "Failed to load classes",
        });
      } finally {
        this.loadingReportOptions = false;
      }
    },

    async loadReportBusChanges() {
      try {
        this.loadingReportBusChanges = true;

        if (!this.busRoutes || this.busRoutes.length === 0) {
          await this.loadBusRoutes();
        }

        const useBuilding = this.useDayCampBuildingFilter();
        const branchValue =
          !useBuilding && this.userBranch ? this.userBranch : "";
        const buildingValue = useBuilding
          ? this.userDayCampBuildings.join(",")
          : "";
        const url =
          "odata/busChange?branch=" +
          branchValue +
          "&building=" +
          buildingValue +
          "&includeCompleted=true&includeDeleted=true";

        const response = await this.$http.get(url);

        this.reportBusChangesFullData = response.value;

        this.reportBusChanges = response.value.map((bc) => {
          let busNumber = "";
          if (bc.pickupRouteID) {
            const pickupRoute = this.busRoutes.find(
              (r) => r.id === bc.pickupRouteID
            );
            if (pickupRoute) {
              busNumber = this.getRouteBusNumber(pickupRoute);
            }
          }
          if (!busNumber && bc.dropoffRouteID) {
            const dropoffRoute = this.busRoutes.find(
              (r) => r.id === bc.dropoffRouteID
            );
            if (dropoffRoute) {
              busNumber = this.getRouteBusNumber(dropoffRoute);
            }
          }

          return {
            id: bc.id,
            studentID: bc.studentID,
            studentName: bc.studentName || "",
            studentNameHeb: bc.studentNameHeb || "",
            building: bc.building || "",
            branch: bc.branch || "",
            class: bc.class || "",
            busNumber: busNumber,
            startDate: bc.startDate,
            endDate: bc.endDate,
            enteredOn: bc.enteredOn,
            familyID: bc.familyID,
            status: bc.status,
            busChangeName: bc.busChangeName || "",
            busChangeAddress: bc.busChangeAddress || "",
            pickupRouteID: bc.pickupRouteID,
            dropoffRouteID: bc.dropoffRouteID,
            reportDownloaded: bc.reportDownloaded === true,
            busRabbiReportDownloaded: bc.busRabbiReportDownloaded === true,
            finished: bc.finished === true,
            finishedReportDownloaded: bc.finishedReportDownloaded === true,
          };
        });

        this.applyReportFilters();
      } catch (error) {
        console.error("Error loading bus changes:", error);
        this.$notify({
          type: "error",
          text: "Failed to load bus changes",
        });
      } finally {
        this.loadingReportBusChanges = false;
      }
    },

    applyReportFilters() {
      let filtered = [...this.reportBusChanges];

      if (!this.showCompletedBusChanges) {
        filtered = filtered.filter((bc) => {
          if (!bc) return false;
          // Finished today must still appear for end-of-day reports
          if (this.isFinishedToday(bc)) return true;
          return (
            bc.status && bc.status.trim().toUpperCase() !== "COMPLETED"
          );
        });
      }

      if (!this.showDeletedBusChanges) {
        filtered = filtered.filter(
          (bc) =>
            bc && bc.status && bc.status.trim().toUpperCase() !== "DELETED"
        );
      }

      if (this.onlyNotDownloadedBusChanges) {
        filtered = filtered.filter((bc) =>
          this.shouldKeepForNotDownloadedFilter(bc)
        );
      }

      if (this.reportFilterBranch) {
        filtered = filtered.filter(
          (bc) => bc.branch === this.reportFilterBranch
        );
      }

      if (this.reportFilterBuildings && this.reportFilterBuildings.length > 0) {
        const wanted = this.reportFilterBuildings.map((b) =>
          String(b).toUpperCase()
        );
        filtered = filtered.filter((bc) =>
          wanted.includes(String((bc && bc.building) || "").toUpperCase())
        );
      }

      if (this.reportFilterClass && this.reportFilterClass.length > 0) {
        filtered = filtered.filter((bc) =>
          this.reportFilterClass.includes(bc.class)
        );
      }

      if (this.reportFilterBusNumber && this.reportFilterBusNumber.length > 0) {
        filtered = filtered.filter((bc) =>
          this.reportFilterBusNumber.includes(bc.busNumber)
        );
      }

      if (this.reportFilterStartDate && this.reportFilterEndDate) {
        filtered = filtered.filter((bc) => {
          const busChangeStart = this.formatDateForPicker(bc.startDate);
          const busChangeEnd = bc.endDate
            ? this.formatDateForPicker(bc.endDate)
            : "9999-12-31";
          return (
            busChangeStart <= this.reportFilterEndDate &&
            busChangeEnd >= this.reportFilterStartDate
          );
        });
      } else if (this.reportFilterStartDate) {
        filtered = filtered.filter((bc) => {
          const busChangeEnd = bc.endDate
            ? this.formatDateForPicker(bc.endDate)
            : "9999-12-31";
          return busChangeEnd >= this.reportFilterStartDate;
        });
      } else if (this.reportFilterEndDate) {
        filtered = filtered.filter((bc) => {
          const busChangeStart = this.formatDateForPicker(bc.startDate);
          return busChangeStart <= this.reportFilterEndDate;
        });
      }

      this.filteredReportBusChanges = filtered;
      this.reportPagination.page = 1;
      this.selectedReportBusChanges = [...filtered];
    },

    clearStartDateFilter() {
      this.reportFilterStartDate = null;
      this.$nextTick(() => {
        this.applyReportFilters();
      });
    },

    clearEndDateFilter() {
      this.reportFilterEndDate = null;
      this.$nextTick(() => {
        this.applyReportFilters();
      });
    },

    selectAllReportBusChanges() {
      this.selectedReportBusChanges = [...this.filteredReportBusChanges];
    },

    clearAllReportBusChanges() {
      this.selectedReportBusChanges = [];
    },

    formatDate(date) {
      if (!date) return "";
      return moment(date).format("MM/DD/YYYY");
    },

    // Reads just the "YYYY-MM-DD" calendar digits from a date value rather
    // than letting moment re-interpret any (possibly mislabeled) timezone
    // offset — everything in this app is a local/Eastern calendar date, so
    // comparing raw digits avoids off-by-one-day results near midnight.
    toLocalDateKey(value) {
      if (!value) return null;
      if (typeof value === "string") {
        const m = value.match(/^(\d{4}-\d{2}-\d{2})/);
        if (m) return m[1];
      }
      return moment(value).format("YYYY-MM-DD");
    },

    isEnteredToday(bc) {
      const entered = bc && (bc.enteredOn || bc.EnteredOn);
      if (!entered) return false;
      return this.toLocalDateKey(entered) === moment().format("YYYY-MM-DD");
    },

    isStartingToday(bc) {
      const start = bc && (bc.startDate || bc.StartDate);
      if (!start) return false;
      return this.toLocalDateKey(start) === moment().format("YYYY-MM-DD");
    },

    isFinishedToday(bc) {
      const finished = bc && (bc.finished === true || bc.Finished === true);
      if (!finished) return false;
      const end = bc.endDate || bc.EndDate;
      if (!end) return false;
      return this.toLocalDateKey(end) === moment().format("YYYY-MM-DD");
    },

    isEndedBusChange(bc) {
      return !!(bc && (bc.finished === true || bc.Finished === true));
    },

    isSingleDayBusChange(bc) {
      const start = bc && (bc.startDate || bc.StartDate);
      const end = bc && (bc.endDate || bc.EndDate);
      if (!start || !end) return true;
      return (
        moment(end)
          .startOf("day")
          .diff(moment(start).startOf("day"), "days") <= 1
      );
    },

    async loadBusChangeGrades() {
      try {
        const response = await this.$http.get("api/busChange/getBusChangeGrades");
        this.busChangeGrades = Array.isArray(response) ? response : [];
      } catch (e) {
        this.busChangeGrades = [];
      }
    },

    gradeUsesExtendedSticker(bc) {
      if (!bc) return true;
      const source = String(bc.source || bc.Source || "")
        .trim()
        .toLowerCase();
      const isDayCamp = source === "day camp" || source === "daycamp";
      const gradeName = String(bc.class || bc.Class || "").trim();
      const scope = String(
        (isDayCamp ? bc.building || bc.Building : bc.branch || bc.Branch) || ""
      ).trim();
      if (!gradeName || !scope) return true;
      const match = (this.busChangeGrades || []).find((g) => {
        const gSource = String((g && g.source) || "")
          .trim()
          .toLowerCase();
        const gDayCamp = gSource === "day camp" || gSource === "daycamp";
        if (gDayCamp !== isDayCamp) return false;
        if (
          String((g && g.grade) || "")
            .trim()
            .toLowerCase() !== gradeName.toLowerCase()
        )
          return false;
        const key = isDayCamp ? g.building : g.branch;
        return String(key || "").trim() === scope;
      });
      if (!match) return true;
      return match.extendedSticker !== false;
    },

    isExtendedBusChange(bc) {
      if (!bc) return false;
      // Ended changes print as a single-day return label, not an extended sticker.
      if (bc.finished === true || bc.Finished === true) return false;
      const start = bc.startDate || bc.StartDate;
      const end = bc.endDate || bc.EndDate;
      if (!start || !end) return false;
      return (
        moment(end)
          .startOf("day")
          .diff(moment(start).startOf("day"), "days") > 1
      );
    },

    shouldKeepForNotDownloadedFilter(bc) {
      if (!bc) return false;
      if (this.isEnteredToday(bc)) return true;
      if (this.isStartingToday(bc)) return true;
      if (this.isFinishedToday(bc)) return true;
      if (this.isSingleDayBusChange(bc)) return true;
      const finished = bc.finished === true || bc.Finished === true;
      const finishedDownloaded =
        bc.finishedReportDownloaded === true ||
        bc.FinishedReportDownloaded === true;
      if (finished && !finishedDownloaded) return true;
      const reportDownloaded =
        bc.reportDownloaded === true || bc.ReportDownloaded === true;
      const busRabbiDownloaded =
        bc.busRabbiReportDownloaded === true ||
        bc.BusRabbiReportDownloaded === true;
      return !reportDownloaded && !busRabbiDownloaded;
    },

    formatDateForPicker(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },

    getStatusIcon(status) {
      if (!status) return "";
      switch (status.trim().toUpperCase()) {
        case "NEW":
          return "mdi-new-box";
        case "FUTURE":
        case "PENDING":
          return "mdi-clock-outline";
        case "ACTIVE":
          return "mdi-check-circle";
        case "COMPLETED":
          return "mdi-check-all";
        case "DELETED":
          return "mdi-delete";
        default:
          return "mdi-information";
      }
    },

    getStatusIconColor(status) {
      if (!status) return "";
      switch (status.trim().toUpperCase()) {
        case "NEW":
          return "#2196f3";
        case "FUTURE":
        case "PENDING":
          return "#ff9800";
        case "ACTIVE":
          return "rgb(92, 184, 96)";
        case "COMPLETED":
          return "#9e9e9e";
        case "DELETED":
          return "#f44336";
        default:
          return "#757575";
      }
    },

    async loadReportBusNumbers() {
      if (this.reportBusNumbers.length > 0) return;

      try {
        this.loadingReportOptions = true;

        if (!this.busRoutes.length) {
          await this.loadBusRoutes();
        }

        this.reportBusNumbers = [
          ...new Set(this.busRoutes.map((r) => this.getRouteBusNumber(r))),
        ]
          .filter((n) => n)
          .sort((a, b) => {
            const numA = parseInt(a);
            const numB = parseInt(b);
            if (!isNaN(numA) && !isNaN(numB)) {
              return numA - numB;
            }
            return a.localeCompare(b);
          });
      } catch (error) {
        console.error("Error loading bus numbers:", error);
        this.$notify({
          type: "error",
          text: "Failed to load bus numbers",
        });
      } finally {
        this.loadingReportOptions = false;
      }
    },

    /**
     * Opens the PDF and triggers print. Content is already inset in the PDF
     * so typical printer non-printable margins do not clip the edges.
     * Prefer "Fit" / "Fit to printable area" in the print dialog when available.
     */
    openPdfPrintWindow(pdfBlob) {
      const blobUrl = window.URL.createObjectURL(pdfBlob);
      // view=Fit helps the built-in viewer show the full page; print uses PDF page size.
      const printWindow = window.open(blobUrl + "#view=Fit", "_blank");

      if (!printWindow) {
        return;
      }

      let printed = false;
      const tryPrint = () => {
        if (printed) return;
        printed = true;
        try {
          printWindow.focus();
          printWindow.print();
        } catch (e) {
          console.warn("Auto-print failed:", e);
        }
      };
      printWindow.addEventListener("load", tryPrint);
      setTimeout(tryPrint, 750);
    },

    async downloadReport(reportType) {
      if (this.selectedReportBusChanges.length === 0) {
        this.$notify({
          type: "error",
          text: "Please select at least one bus change",
        });
        return;
      }

      try {
        this.downloadingReport = reportType;

        const reportEndpoints = {
          labels30: "GenerateClassReportPdf",
          labelSize: "GenerateLabelSizeReportPdf",
        };
        const endpoint = reportEndpoints[reportType];
        if (!endpoint) {
          this.$notify({
            group: "notification",
            type: "error",
            text: "Unknown report type",
          });
          return;
        }

        const apiUrl = `api/buschange/${endpoint}`;

        let fullBusChangesData = this.sortedReportBusChanges
          .filter((displayItem) =>
            this.selectedReportBusChanges.some(
              (selected) => selected.id === displayItem.id
            )
          )
          .map((displayItem) => {
            return this.reportBusChangesFullData.find(
              (fullData) => fullData.id === displayItem.id
            );
          })
          .filter((item) => item);

        const isExtended = (bc) => this.isExtendedBusChange(bc);

        if (this.onlyNotDownloadedBusChanges) {
          fullBusChangesData = fullBusChangesData.filter((bc) =>
            this.shouldKeepForNotDownloadedFilter(bc)
          );
          if (fullBusChangesData.length === 0) {
            this.$notify({
              group: "notification",
              type: "error",
              text:
                "No bus changes to include. All selected records have already been downloaded for this report.",
            });
            return;
          }
        }

        if (reportType === "labels30") {
          // Ended changes print as single-day return labels, not stickers.
          let extendedOnly = fullBusChangesData.filter(isExtended);
          const singleDayCardGrades = extendedOnly.filter(
            (bc) => !this.gradeUsesExtendedSticker(bc)
          );
          extendedOnly = extendedOnly.filter((bc) =>
            this.gradeUsesExtendedSticker(bc)
          );
          if (this.onlyNotDownloadedBusChanges) {
            extendedOnly = extendedOnly.filter((bc) => {
              if (this.isEnteredToday(bc) || this.isStartingToday(bc)) {
                return true;
              }
              return bc.reportDownloaded !== true;
            });
          }
          if (extendedOnly.length === 0) {
            this.$notify({
              group: "notification",
              type: "error",
              text: singleDayCardGrades.length
                ? "No extended stickers for this download. These grades are set to use the regular card on the first day of an extended bus change. Use Single-Day & Bus Rabbi instead."
                : "None of the selected bus changes need extended stickers. Use Single-Day & Bus Rabbi.",
            });
            return;
          }
          fullBusChangesData = extendedOnly;
        }

        const response = await this.$http.post(apiUrl, fullBusChangesData, {
          responseType: "blob",
        });

        const rawBlob =
          response instanceof Blob ? response : new Blob([response]);

        const reportTypeNames = {
          labels30: "Extended Bus Changes",
          labelSize: "Single-Day & Bus Rabbi",
        };
        const reportTypeName = reportTypeNames[reportType] || "Report";

        const pdfBlob = new Blob([rawBlob], { type: "application/pdf" });
        this.openPdfPrintWindow(pdfBlob);

        this.$notify({
          type: "success",
          text: `${reportTypeName} report opened — print dialog should appear`,
        });

        await this.loadReportBusChanges();
      } catch (error) {
        console.error("Error downloading report:", error);
      } finally {
        this.downloadingReport = null;
      }
    },
  },
};
</script>

<style scoped>
.dialog-header {
  background: linear-gradient(135deg, rgb(92, 184, 96) 0%, #388e3c 100%);
  color: white;
  padding: 0px 16px;
}

.dialog-title {
  font-size: 20px;
  font-weight: 400;
}

.report-table-container {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background: white;
}

.report-table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 16px;
  background: #f5f5f5;
  border-bottom: 1px solid #e0e0e0;
  min-height: 36px;
}

.report-table-scroll {
  max-height: 400px;
  overflow-y: auto;
  min-height: 180px;
}

.report-list-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 180px;
}

.report-table-pagination {
  padding: 12px 16px;
  border-top: 1px solid #e0e0e0;
  background: #fafafa;
}

.report-data-table {
  border-radius: 0 !important;
}

.bus-change-main-info {
  font-weight: 500;
  color: #212121;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}

.report-ended-chip {
  display: inline-flex;
  align-items: center;
  font-size: 10px;
  font-weight: 700;
  color: #ffffff;
  background: linear-gradient(135deg, #e53935, #b71c1c);
  border-radius: 10px;
  padding: 1px 7px;
  letter-spacing: 0.2px;
}

.report-ended-chip-today {
  background: linear-gradient(135deg, #fb8c00, #e65100);
}

.bus-change-sub-info {
  font-size: 11px;
  color: #757575;
  margin-top: 3px;
  line-height: 1.5;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.bus-change-sub-info span {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
}

.bus-change-sub-info .v-icon {
  vertical-align: middle;
  margin-right: 2px;
}

.report-filters-row {
  flex-wrap: nowrap !important;
}

.report-filter-building {
  flex: 0 0 168px !important;
  max-width: 168px !important;
  min-width: 168px !important;
}

.report-filter-narrow {
  flex: 0 0 108px !important;
  max-width: 108px !important;
  min-width: 108px !important;
}

.report-filter-grade {
  flex: 0.65 1 0 !important;
  max-width: 220px !important;
  min-width: 0 !important;
}

.report-filter-date {
  flex: 0 0 142px !important;
  max-width: 142px !important;
  min-width: 142px !important;
}

.report-filter-main {
  flex: 1 1 0 !important;
  min-width: 0 !important;
}
</style>
